(() => {
  const all = (root, selector) =>
    Array.from(root.querySelectorAll(selector));

  function findTemplates(productId) {
    return all(
      document,
      `[data-scotts-palette-template][data-product-id="${productId}"]`
    );
  }

  function findProductForm(mount) {
    const formId = mount.dataset.productFormId;

    if (formId) {
      const exact = document.getElementById(formId);

      if (exact && exact.tagName === 'FORM') {
        return exact;
      }
    }

    const productInfo = mount.closest('product-info');

    if (productInfo) {
      const form = productInfo.querySelector(
        'product-form form[data-type="add-to-cart-form"], form[id^="product-form-"]'
      );

      if (form) {
        return form;
      }
    }

    return null;
  }

  function ensurePropertyInput(form, palette) {
    if (!form) return null;

    const paletteId = palette.dataset.paletteId;

    let input = form.querySelector(
      `[data-scotts-property-input="${paletteId}"]`
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${palette.dataset.propertyName}]`;
      input.dataset.scottsPropertyInput = paletteId;

      form.appendChild(input);
    }

    return input;
  }

  function initialisePalette(palette, mount) {
    if (palette.dataset.initialised === 'true') {
      return;
    }

    palette.dataset.initialised = 'true';

    const modal = palette.querySelector(
      '[data-scotts-palette-modal]'
    );

    const open = palette.querySelector(
      '[data-scotts-palette-open]'
    );

    const closes = all(
      palette,
      '[data-scotts-palette-close]'
    );

    const swatches = all(
      palette,
      '[data-scotts-palette-swatch]'
    );

    const confirm = palette.querySelector(
      '[data-scotts-palette-confirm]'
    );

    const pendingName = palette.querySelector(
      '[data-scotts-pending-name]'
    );

    const selectedName = palette.querySelector(
      '[data-scotts-selected-name]'
    );

    const selectedSwatch = palette.querySelector(
      '[data-scotts-selected-swatch]'
    );

    const error = palette.querySelector(
      '[data-scotts-palette-error]'
    );

    let pending = null;

    const openModal = () => {
      if (!modal) return;

      /*
       * Important:
       * move the popup outside the product-info column.
       * Otherwise Dawn constrains it to the right-hand
       * product column and the swatches become tiny.
       */
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }

      modal.hidden = false;

      document.body.classList.add(
        'scotts-palette-modal-open'
      );
    };

    const closeModal = () => {
      if (!modal) return;

      modal.hidden = true;

      document.body.classList.remove(
        'scotts-palette-modal-open'
      );
    };

    const choose = (swatch) => {
      pending = swatch;

      swatches.forEach((item) => {
        const active = item === swatch;

        item.classList.toggle(
          'is-selected',
          active
        );

        item.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );
      });

      if (pendingName) {
        pendingName.textContent =
          swatch.dataset.value || '';
      }

      if (confirm) {
        confirm.disabled = false;
      }
    };

    const commit = () => {
      if (!pending) return;

      const value =
        pending.dataset.value || '';

      const form =
        findProductForm(mount);

      const propertyInput =
        ensurePropertyInput(
          form,
          palette
        );

      if (propertyInput) {
        propertyInput.value = value;
      }

      palette.dataset.selected = 'true';

      if (selectedName) {
        selectedName.textContent = value;
      }

      if (error) {
        error.hidden = true;
      }

      if (selectedSwatch) {
        selectedSwatch.innerHTML = '';

        const image =
          pending.querySelector('img');

        if (image) {
          const clone =
            image.cloneNode(true);

          clone.removeAttribute('width');
          clone.removeAttribute('height');

          selectedSwatch.appendChild(
            clone
          );

          selectedSwatch.hidden = false;
        } else {
          selectedSwatch.hidden = true;
        }
      }

      closeModal();
    };

    if (open) {
      open.addEventListener(
        'click',
        openModal
      );
    }

    closes.forEach((button) => {
      button.addEventListener(
        'click',
        closeModal
      );
    });

    swatches.forEach((swatch) => {
      swatch.addEventListener(
        'click',
        () => choose(swatch)
      );
    });

    if (confirm) {
      confirm.addEventListener(
        'click',
        commit
      );
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          modal &&
          !modal.hidden
        ) {
          closeModal();
        }
      }
    );
  }

  function validateMount(mount) {
    const palettes = all(
      mount,
      '[data-scotts-palette][data-required="true"]'
    );

    let valid = true;
    let firstInvalid = null;

    palettes.forEach((palette) => {
      const selected =
        palette.dataset.selected === 'true';

      const error =
        palette.querySelector(
          '[data-scotts-palette-error]'
        );

      if (error) {
        error.hidden = selected;
      }

      if (!selected) {
        valid = false;

        if (!firstInvalid) {
          firstInvalid = palette;
        }
      }
    });

    if (firstInvalid) {
      firstInvalid.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    return valid;
  }

  function wireValidation(mount) {
    const form =
      findProductForm(mount);

    if (
      !form ||
      form.dataset.scottsPaletteValidation === 'true'
    ) {
      return;
    }

    form.dataset.scottsPaletteValidation =
      'true';

    form.addEventListener(
      'submit',
      (event) => {
        if (!validateMount(mount)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  }

  function hydrateMount(mount) {
    const productId =
      mount.dataset.productId;

    if (!productId) {
      return false;
    }

    const templates =
      findTemplates(productId);

    if (!templates.length) {
      return false;
    }

    const currentIds = all(
      mount,
      '[data-scotts-palette]'
    ).map(
      (palette) =>
        palette.dataset.paletteId
    );

    const templateIds =
      templates.map(
        (template) =>
          template.dataset.paletteId
      );

    const same =
      currentIds.length === templateIds.length &&
      currentIds.every(
        (id, index) =>
          id === templateIds[index]
      );

    if (!same) {
      mount.innerHTML = '';

      templates.forEach(
        (template) => {
          mount.appendChild(
            template.content.cloneNode(true)
          );
        }
      );
    }

    all(
      mount,
      '[data-scotts-palette]'
    ).forEach((palette) => {
      initialisePalette(
        palette,
        mount
      );
    });

    wireValidation(mount);

    mount.dataset.hydrated =
      'true';

    return true;
  }

  function hydrateAll() {
    all(
      document,
      '[data-scotts-virtual-options-mount]'
    ).forEach(hydrateMount);
  }

  document.addEventListener(
    'DOMContentLoaded',
    hydrateAll
  );

  document.addEventListener(
    'shopify:section:load',
    hydrateAll
  );

  document.addEventListener(
    'shopify:block:select',
    hydrateAll
  );

  document.addEventListener(
    'shopify:block:deselect',
    hydrateAll
  );

  /*
   * Shopify Theme Editor may load
   * the palette-library section and
   * product section at different times.
   */
  let attempts = 0;

  const retry =
    window.setInterval(() => {
      hydrateAll();

      attempts += 1;

      if (attempts >= 20) {
        window.clearInterval(
          retry
        );
      }
    }, 500);

  /*
   * Also watch for dynamically
   * re-rendered Shopify sections.
   */
  const observer =
    new MutationObserver(
      hydrateAll
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();